import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Bot, Target, Zap, Activity, Loader, AlertCircle, Wallet, Plus, Settings, Play, Pause, Trash2, Edit2, ToggleLeft, ToggleRight, History, TrendingUp, Info, Copy, Check, Search, ExternalLink, Globe, MessageCircle, Youtube, Pill, X } from 'lucide-react';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import { wsService } from '../../services/websocketService';
import { useWalletUser } from '../../hooks/useWalletUser';
import { getUserTradingWallets, restoreTradingWalletKeypair } from '../../services/tradingWalletService';
import { getBalance } from '../../services/shyftService';
import { tradeEngine } from '../../services/tradeEngine';
const SOL_MINT = 'So11111111111111111111111111111111111111112';
import { 
  autoTraderRulesService, 
  AutoTraderRule, 
  AutoTraderCondition,
  AUTO_TRADER_FIELD_LABELS
} from '../../services/autoTraderRulesService';
import { adminSupabase } from '../../services/supabaseService';
import GlobalDropdown from '../../components/GlobalDropdown';
import { userService } from '../../services/userService';
import { createAdvancedChartsWidget } from '../AdvancedCharts/AdvancedCharts';
import { createTokenInfoWidget } from '../TokenInfo/TokenInfo';
import { Token } from '../../types/supabase';
import TokenSearchModal from '../../components/TokenSearchModal';

// Types for auto trader rules (similar to sniper but for specific tokens)
interface LocalAutoTraderRule {
  id: string;
  name: string;
  isActive: boolean;
  conditions: LocalAutoTraderCondition[];
  createdAt: string;
  tokenAddress: string; // Key difference: specific token
  tokenSymbol?: string;
  tokenName?: string;
  tradeType: 'buy' | 'sell'; // New field for trade type
  // Configuration from database
  solAmountPerTrade?: number;
  maxTradesToExecute?: number;
  slippagePercent?: number;
  tradingWalletAddresses?: string[];
  totalSolBudget?: number;
  configurationMetadata?: any;
}

interface LocalAutoTraderCondition {
  field: 'marketCap' | 'liquidity' | 'holders' | 'buys' | 'sells' | 'curvePercentage' | 'txAmount' | 'txVolume' | 'name' | 'symbol';
  operator: '>' | '<' | '>=' | '<=' | '=' | 'increase%' | 'decrease%';
  value: number;
}

interface TradingWallet {
  id: string;
  name: string;
  public_key: string;
  created_at: string;
  owner_wallet_address: string;
  is_active: boolean;
  last_used_at?: string;
  balance?: number;
}

interface SelectedWallet extends TradingWallet {
  solBalance: number;
  isSelected: boolean;
}

// Define the props for the AdvancedAutoTrader component
export interface AdvancedAutoTraderProps {
  onClose?: () => void;
  isWidget?: boolean;
  onAddWidget?: (widget: any) => void; // For adding widgets to canvas
}

const AdvancedAutoTrader: React.FC<AdvancedAutoTraderProps> = ({
  onClose,
  isWidget = false,
  onAddWidget
}) => {
  const walletUser = useWalletUser();
  
  // Custom notification functions to replace react-hot-toast
  const showNotification = (message: string, type: 'success' | 'error' | 'loading', id?: string) => {
    const toastId = id || `autotrader-${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    
    window.dispatchEvent(new CustomEvent('ekko:show_toast', {
      detail: {
        message,
        type,
        id: toastId
      }
    }));
    
    return toastId;
  };

  const hideNotification = (id: string, type?: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:hide_toast', {
      detail: {
        id,
        type
      }
    }));
  };

  const updateNotification = (id: string, message: string, type: 'success' | 'error' | 'loading') => {
    window.dispatchEvent(new CustomEvent('ekko:update_toast', {
      detail: {
        id,
        message,
        type
      }
    }));
  };

  // Helper function to get effective wallet address (use direct connection)
  const getEffectiveWalletAddress = (): string | null => {
    // Use direct wallet connection since it's always reliable
    if (typeof window !== 'undefined' && window.solana?.publicKey) {
      return window.solana.publicKey.toString();
    }
    
    // Fallback to hook only if direct connection fails
    if (walletUser.walletAddress && walletUser.walletAddress.trim() !== '') {
      console.warn('⚠️ Using hook as fallback (direct wallet unavailable):', walletUser.walletAddress);
      return walletUser.walletAddress;
    }
    
    return null;
  };
  
  // State management
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error'>('disconnected');
  const [isLoading, setIsLoading] = useState(false);
  const subscribedTokensRef = useRef<Set<string>>(new Set());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const tokenSubscriptionsRef = useRef<Map<string, (() => void)[]>>(new Map());
  const tokenMetadataRef = useRef<Map<string, { name: string; symbol: string }>>(new Map());
  const masterTokenDataRef = useRef<Map<string, any>>(new Map());

  // Trading wallet management
  const [tradingWallets, setTradingWallets] = useState<TradingWallet[]>([]);
  const [selectedWallets, setSelectedWallets] = useState<Set<string>>(new Set());
  const [walletBalances, setWalletBalances] = useState<Map<string, number>>(new Map());
  const [isLoadingWallets, setIsLoadingWallets] = useState(false);

  // Auto trader configuration
  const [solAmountPerTrade, setSolAmountPerTrade] = useState<string>('0.1');
  const [maxTradesToExecute, setMaxTradesToExecute] = useState<string>('5');
  const [slippagePercent, setSlippagePercent] = useState<string>('35');
  const [isTradingActive, setIsTradingActive] = useState(false);
  const isTradingActiveRef = useRef<boolean>(false);

  // Rules management
  const [autoTraderRules, setAutoTraderRules] = useState<LocalAutoTraderRule[]>([]);
  const autoTraderRulesRef = useRef<LocalAutoTraderRule[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);

  // Current view state
  const [activeTab, setActiveTab] = useState<'monitor' | 'transactions'>('monitor');

  // Load trade history when transactions tab is selected
  useEffect(() => {
    if (activeTab === 'transactions') {
      loadTradeHistory();
    }
  }, [activeTab]);
  
  // Auto trader bot creation flow (like SniperBot)
  const [isCreatingBot, setIsCreatingBot] = useState(false);
  const [creationStep, setCreationStep] = useState<'token' | 'rules' | 'config'>('token');
  const [newBotName, setNewBotName] = useState('');
  const [newBotRules, setNewBotRules] = useState<LocalAutoTraderCondition[]>([]);
  const [editingBotId, setEditingBotId] = useState<string | null>(null);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  
  // Deletion confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<{ id: string; name: string; isActive: boolean } | null>(null);

  // Performance optimization refs
  const maxTrackedTokens = 50; // Increased limit for better tracking
  const cleanupThreshold = 47; // Start cleanup before hitting the limit
  const tokensToRemoveCount = 10; // Remove first 10 tokens for efficiency

  // Total tokens tracked over time (frontend counter) - USE REF to prevent re-renders
  const totalTokensTrackedRef = useRef<number>(0);
  const [displayTotalTokensTracked, setDisplayTotalTokensTracked] = useState(0);

  // Trade tracking per rule (ruleId -> count)
  const ruleTradeCountRef = useRef<Map<string, number>>(new Map());
  
  // Track recently traded tokens to prevent duplicates (tokenAddress -> timestamp)
  const recentlyTradedTokensRef = useRef<Map<string, number>>(new Map());
  const DUPLICATE_PREVENTION_TIME = 60000; // 1 minute
  
  // Track wallet rotation for round-robin distribution (ruleId -> walletIndex)
  const walletRotationRef = useRef<Map<string, number>>(new Map());
  
  // Track executing trades per rule to prevent race conditions (ruleId -> Promise)
  const ruleExecutionLockRef = useRef<Map<string, Promise<void>>>(new Map());

  // Track baseline values for percentage calculations (ruleId -> tokenAddress -> fieldName -> baselineValue)
  const ruleBaselinesRef = useRef<Map<string, Map<string, Map<string, number>>>>(new Map());

  // Trade history management
  const [tradeHistory, setTradeHistory] = useState<any[]>([]);
  const [isLoadingTradeHistory, setIsLoadingTradeHistory] = useState(false);
  const realtimeSubscriptionRef = useRef<any>(null);

  // Rule statistics (ruleId -> {triggers: number, success: number, failed: number})
  const [ruleStatistics, setRuleStatistics] = useState<Map<string, {triggers: number, success: number, failed: number}>>(new Map());

  // Copy to clipboard state
  const [copiedTokens, setCopiedTokens] = useState<Set<string>>(new Set());

  // Copy token address to clipboard
  const copyTokenAddress = async (tokenAddress: string) => {
    try {
      await navigator.clipboard.writeText(tokenAddress);
      setCopiedTokens(prev => new Set([...prev, tokenAddress]));
      
      // Remove from copied set after 2 seconds
      setTimeout(() => {
        setCopiedTokens(prev => {
          const newSet = new Set(prev);
          newSet.delete(tokenAddress);
          return newSet;
        });
      }, 2000);
      
      showNotification(`Token address copied to clipboard!`, 'success');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showNotification(`Failed to copy address`, 'error');
    }
  };

  // Render token socials (identical to SniperBot)
  const renderTokenSocials = (tokenData: any, tokenAddress: string, tokenSymbol: string) => {
    if (!tokenData?.token) return null;

    const { token } = tokenData;
    const socials = token.strictSocials || {};
    
    return (
      <div className="mb-2">
        {/* Social Links - Exact TokenCard styling */}
        <div className="flex gap-1">
          {/* X.com Search */}
          <a
            href={`https://x.com/search?q=${tokenSymbol}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="Search on Twitter"
          >
            <Search size={10} className="text-white" />
          </a>

          {/* Pump.fun pill */}
          <a
            href={`https://pump.fun/coin/${tokenAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
            title="View on Pump.fun"
          >
            <Pill size={10} className="text-white" />
          </a>

          {/* Twitter/X Official */}
          {socials.twitter && (
            <a
              href={socials.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Twitter"
            >
              <img 
                src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                alt="X" 
                className="w-2.5 h-2.5 invert"
              />
            </a>
          )}

          {/* Telegram */}
          {socials.telegram && (
            <a
              href={socials.telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-[#0088cc] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Telegram"
            >
              <MessageCircle size={10} />
            </a>
          )}

          {/* YouTube */}
          {(token.youtube || socials.youtube) && (
            <a
              href={token.youtube || socials.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-[#FF0000] rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official YouTube"
            >
              <Youtube size={10} />
            </a>
          )}

          {/* Website */}
          {socials.website && (
            <a
              href={socials.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white bg-gray-600 rounded-full w-4 h-4 flex items-center justify-center hover:opacity-80"
              title="Official Website"
            >
              <Globe size={10} />
            </a>
          )}
        </div>
      </div>
    );
  };

  // Memoized Timer component for transactions to prevent re-renders (identical to SniperBot)
  const TransactionTimer = React.memo<{ createdAt: string }>(({ createdAt }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
      const updateElapsed = () => {
        const now = new Date().getTime();
        const created = new Date(createdAt).getTime();
        const diff = now - created;

        if (diff < 0) {
          setElapsed('now');
          return;
        }

        const seconds = Math.floor(diff / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
          setElapsed(`${days}d`);
        } else if (hours > 0) {
          setElapsed(`${hours}h`);
        } else if (minutes > 0) {
          setElapsed(`${minutes}m`);
        } else {
          setElapsed(`${seconds}s`);
        }
      };

      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);

      return () => clearInterval(interval);
    }, [createdAt]);

    return (
      <div className="text-[#24c35b] text-xs font-bold text-center">
        {elapsed}
      </div>
    );
  });

  // Memoized Token info component for transactions to prevent re-renders (identical to SniperBot)
  const TokenInfoDisplay = React.memo<{ tokenAddress: string; tokenSymbol: string }>(({ tokenAddress, tokenSymbol }) => {
    const [tokenData, setTokenData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
      const loadTokenData = async () => {
        if (tokenDataCacheRef.current.has(tokenAddress)) {
          setTokenData(tokenDataCacheRef.current.get(tokenAddress));
          return;
        }

        setIsLoading(true);
        const data = await fetchTokenData(tokenAddress);
        if (data) {
          setTokenData(data);
        }
        setIsLoading(false);
      };

      loadTokenData();
    }, [tokenAddress]);

    if (isLoading) {
      return (
        <div className="flex items-center gap-1 mb-2">
          <Loader size={10} className="text-gray-400 animate-spin" />
          <span className="text-gray-400 text-[10px]">Loading token info...</span>
        </div>
      );
    }

    if (!tokenData) {
      return null;
    }

    return renderTokenSocials(tokenData, tokenAddress, tokenSymbol);
  });

  // Functions to open widgets with specific tokens (identical to SniperBot)
  const openAdvancedChartsWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    // Create a properly formed token object with default values that won't break the chart
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields (will be updated by the chart widget)
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields that might be needed
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // FIRST: Dispatch token selection events to update existing chart widgets (same as TokenCard)
    // 1. Dispatch the token_selected event that chart widgets listen for
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token: tokenData, force_refresh: true }
    });
    window.dispatchEvent(event);
    
    // 2. Dispatch canvas_update_widgets event specifically for canvas
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
      detail: { token: tokenData, timestamp: Date.now() }
    });
    window.dispatchEvent(canvasUpdateEvent);

    // SECOND: Also create a new Advanced Charts widget if onAddWidget is available
    if (onAddWidget) {
    const widget = createAdvancedChartsWidget(tokenData);
    onAddWidget({
      ...widget,
      position: { x: 100, y: 100 } // Default position
    });
    
    console.log(`📈 Opened Advanced Charts widget for ${tokenSymbol} (${tokenAddress}) - chart will auto-load token data`);
    } else {
      console.log(`📈 Updated existing chart widgets with ${tokenSymbol} (${tokenAddress}) data`);
    }
  };

  const openTokenInfoWidget = (tokenAddress: string, tokenSymbol: string, tokenName?: string) => {
    // Create a properly formed token object for the token info widget
    const tokenData: Token = {
      // Essential identification fields
      mint: tokenAddress,
      symbol: tokenSymbol,
      name: tokenName || tokenSymbol,
      id: tokenAddress,

      // Default values for required fields
      signature: '',
      trader_public_key: '',
      tx_type: 'buy',
      sol_amount: 0,
      token_amount: 0,
      timestamp: new Date().toISOString(),
      price_usd: 0,
      market_cap_usd: 0,
      liquidity_usd: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),

      // Additional fields
      initial_buy: null,
      market_cap_sol: null,
      bonding_curve_key: null,
      v_sol_in_bonding_curve: null,
      v_tokens_in_bonding_curve: null,
      uri: null,
      metadata: null,
      description: null,
      image_uri: null,
      image_url: null,
      twitter: null,
      telegram: null,
      website: null,
      discord: null,
      attributes: null,
      dev_projects: null,
      is_rugged: null,
      rug_date: null,
      rug_type: null,
      buys: null,
      sells: null,
      holders: null,
      volume: null,
      liquidity: null,
      token_price: null,
      snipers: null,
      insiders: null,
      top_10_holders: null,
      bonding_curve_progress: null,
      sequence_number: null,
      volume_usd: null,
      tx_volume: null,
      buys_count: null,
      sells_count: null,
      holders_count: null,
      position: null,
      market_type: null,
      market: null,
      youtube: null,
      is_raydium: undefined,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      volume_24h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // FIRST: Dispatch token selection events to update existing widgets (same as TokenCard)
    // 1. Dispatch the token_selected event that other widgets listen for
    const event = new CustomEvent('ekko:token_selected', { 
      detail: { token: tokenData, force_refresh: true }
    });
    window.dispatchEvent(event);
    
    // 2. Dispatch canvas_update_widgets event specifically for canvas
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', { 
      detail: { token: tokenData, timestamp: Date.now() }
    });
    window.dispatchEvent(canvasUpdateEvent);

    // SECOND: Also create a new Token Info widget if onAddWidget is available
    if (onAddWidget) {
    const widget = createTokenInfoWidget({ selectedToken: tokenData });
    onAddWidget({
      ...widget,
      position: { x: 200, y: 200 } // Default position, offset from charts
    });
    
    console.log(`ℹ️ Opened Token Info widget for ${tokenSymbol} (${tokenAddress}) - widget will load token data`);
    } else {
      console.log(`ℹ️ Updated existing widgets with ${tokenSymbol} (${tokenAddress}) data`);
    }
  };

  // Memoized Transaction Card to prevent unnecessary re-renders (identical to SniperBot)
  const TransactionCard = React.memo<{ trade: any; index: number }>(({ trade, index }) => {
    const tokenData = trade.token_address ? tokenDataCacheRef.current.get(trade.token_address) : null;
    const tokenImage = tokenData?.token?.image;
    
    return (
      <div
        key={trade.id || index}
        className={`relative p-3 rounded-lg border overflow-hidden ${
          trade.status === 'success' 
            ? 'border-[#24c35b]/40 bg-[#24c35b]/5' 
            : trade.status === 'pending'
            ? 'border-yellow-500/40 bg-yellow-500/5'
            : 'border-red-500/40 bg-red-500/5'
        }`}
      >
        {/* Background overlay to control image opacity */}
        {tokenImage && (
          <div 
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: `url(${tokenImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}
        
        {/* Content overlay */}
        <div className="relative z-10">
          {/* Status label - Top right */}
          <div className="absolute top-0 right-0">
            <span className={`px-2 py-1 rounded text-[10px] font-medium ${
              trade.status === 'success' 
                ? 'bg-[#24c35b]/20 text-[#24c35b] border border-[#24c35b]/40' 
                : trade.status === 'pending'
                ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {trade.status.toUpperCase()}
            </span>
          </div>

          <div className="flex items-center mb-2">
            <div className="flex items-center flex-1">
              <div className="flex flex-col flex-1">
                <div className="flex items-center gap-2">
                  {/* Timer to the left of token name */}
                  <TransactionTimer createdAt={trade.created_at} />
                  <span className="text-white text-xs font-medium">
                    {trade.token_name} ({trade.token_symbol})
                  </span>
                  {trade.token_address && (
                    <button
                      onClick={() => copyTokenAddress(trade.token_address)}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy token address"
                    >
                      {copiedTokens.has(trade.token_address) ? (
                        <Check size={12} className="text-green-400" />
                      ) : (
                        <Copy size={12} />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Token Social Links - Above action buttons */}
                {trade.status === 'success' && trade.token_address && (
                  <TokenInfoDisplay 
                    tokenAddress={trade.token_address} 
                    tokenSymbol={trade.token_symbol || 'Unknown'} 
                  />
                )}
                
                {/* Action Buttons for Successful Trades - Now underneath token name */}
                {trade.status === 'success' && trade.token_address && (
                  <div className="space-y-1 mt-1">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => openAdvancedChartsWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/40 transition-all flex items-center"
                        title={`Open Advanced Charts widget for ${trade.token_symbol || 'token'}`}
                      >
                        <TrendingUp size={10} className="mr-1" />
                        Chart
                      </button>
                      <button
                        onClick={() => openTokenInfoWidget(trade.token_address, trade.token_symbol || 'Unknown', trade.token_name)}
                        className="px-2 py-1 rounded text-[10px] bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 border border-purple-500/40 transition-all flex items-center"
                        title={`Open Token Info widget for ${trade.token_symbol || 'token'}`}
                      >
                        <Info size={10} className="mr-1" />
                        Token
                      </button>
                    </div>
                    
                    {/* Triggered Metrics Display */}
                    {trade.triggered_conditions && (
                      <div className="p-1.5 rounded bg-gray-600/10 border border-gray-600/20">
                        <div className="text-[9px] text-gray-400 mb-0.5 font-medium">Triggered Conditions:</div>
                        <div className="space-y-0.5">
                          {trade.triggered_conditions.slice(0, 3).map((condition: LocalAutoTraderCondition, idx: number) => (
                            <div key={idx} className="text-[9px] text-gray-300 font-mono">
                              • {AUTO_TRADER_FIELD_LABELS[condition.field] || condition.field} {condition.operator} {condition.value}
                            </div>
                          ))}
                          {trade.triggered_conditions.length > 3 && (
                            <div className="text-[9px] text-gray-500">
                              +{trade.triggered_conditions.length - 3} more condition{trade.triggered_conditions.length - 3 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-400">Amount:</span>
              <span className="text-white ml-1">{trade.amount_sol} SOL</span>
            </div>
            <div>
              <span className="text-gray-400">Wallet:</span>
              <span className="text-white ml-1 font-mono">
                {trade.trading_wallet_address?.slice(0, 6)}...{trade.trading_wallet_address?.slice(-4)}
              </span>
            </div>
          </div>
          
          {trade.transaction_signature && (
            <div className="mt-2 text-xs flex items-center justify-between">
              <div>
                <span className="text-gray-400">Txn:</span>
                <a
                  href={`https://solscan.io/tx/${trade.transaction_signature}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#24c35b] ml-1 font-mono break-all hover:text-[#2dd865] transition-colors cursor-pointer underline"
                  title="View on Solscan"
                >
                  {trade.transaction_signature.slice(0, 16)}...{trade.transaction_signature.slice(-8)}
                </a>
              </div>
              
              {/* Market info - Same row as transaction, right aligned */}
              {trade.token_address && tokenData && (
                <div>
                  {(() => {
                    const pools = tokenData.pools || [];
                    const primaryPool = pools.find((pool: any) => pool.liquidity?.usd > 0) || pools[0];
                    const marketName = primaryPool?.market || null;
                    
                    if (marketName && marketName !== 'Unknown') {
                      return (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-300 capitalize">
                          {marketName.replace('-', ' ')}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}
          
          {trade.error_message && (
            <div className="mt-2 text-xs">
              <span className="text-red-400">Error:</span>
              <span className="text-red-300 ml-1">{trade.error_message}</span>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Token data cache for transactions - USE REF to prevent re-renders
  const tokenDataCacheRef = useRef<Map<string, any>>(new Map());
  const [, forceUpdate] = useState(0); // Only for manual re-renders when needed
  
  // Loading token data state - USE REF to prevent re-renders
  const loadingTokenDataRef = useRef<Set<string>>(new Set());

  // Handle token selection from search modal
  const handleTokenSelect = (token: Token) => {
    setSelectedToken(token);
    setIsSearchModalOpen(false);
  };

  // Fetch token data from Solana Tracker API
  const fetchTokenData = async (tokenAddress: string) => {
    // Check if already cached
    if (tokenDataCacheRef.current.has(tokenAddress)) {
      return tokenDataCacheRef.current.get(tokenAddress);
    }

    // Check if already loading
    if (loadingTokenDataRef.current.has(tokenAddress)) {
      return null;
    }

    try {
      loadingTokenDataRef.current.add(tokenAddress);

      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        console.error('Solana Tracker API key not found');
        return null;
      }

      const response = await fetch(`https://data.solanatracker.io/tokens/${tokenAddress}`, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Cache the data
      tokenDataCacheRef.current.set(tokenAddress, data);
      
      console.log(`✅ Fetched token data for ${tokenAddress}:`, data.token?.symbol || 'Unknown');
      return data;
    } catch (error) {
      console.error(`Error fetching token data for ${tokenAddress}:`, error);
      return null;
    } finally {
      loadingTokenDataRef.current.delete(tokenAddress);
    }
  };

  // Poll token data every 3 seconds for tracked tokens
  const pollTokenData = async (tokenAddress: string) => {
    try {
      const apiKey = import.meta.env.VITE_SOLANA_TRACKER_API_KEY;
      if (!apiKey) {
        console.error('Solana Tracker API key not found');
        return;
      }

      const response = await fetch(`https://data.solanatracker.io/tokens/${tokenAddress}`, {
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update cache with fresh data
      tokenDataCacheRef.current.set(tokenAddress, data);
      
      // Extract relevant data from the response
      const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
      const primaryPool = data.pools && data.pools.length > 0 ? data.pools[0] : null;
      const tokenInfo = tokenMetadataRef.current.get(tokenAddress);
      
      // Track what changed for logging
      const changes = [];
      const newHolders = data.holders || currentData.holders || 0;
      const newBuys = primaryPool?.txns?.buys ? 
        Math.max(primaryPool.txns.buys, currentData.buys || 0) : 
        (currentData.buys || 0);
      const newSells = primaryPool?.txns?.sells ? 
        Math.max(primaryPool.txns.sells, currentData.sells || 0) : 
        (currentData.sells || 0);
      
      // Log changes
      if (newHolders !== (currentData.holders || 0)) {
        changes.push(`holders: ${currentData.holders || 0} → ${newHolders}`);
      }
      if (newBuys !== (currentData.buys || 0)) {
        changes.push(`buys: ${currentData.buys || 0} → ${newBuys}`);
      }
      if (newSells !== (currentData.sells || 0)) {
        changes.push(`sells: ${currentData.sells || 0} → ${newSells}`);
      }
      
      const updatedData = {
        // Always update holders (main goal)
        holders: newHolders,
        
        // Update buys/sells only if new values are higher
        buys: newBuys,
        sells: newSells,
        
        // Update other useful data
        marketCapUsd: primaryPool?.marketCap?.usd || currentData.marketCapUsd,
        liquidityUsd: primaryPool?.liquidity?.usd || currentData.liquidityUsd,
        priceUsd: primaryPool?.price?.usd || currentData.priceUsd,
        
        // Update transaction totals
        totalTxns: primaryPool?.txns?.total || currentData.totalTxns,
        txVolume: primaryPool?.txns?.volume || currentData.txVolume,
        
        // Add poll timestamp
        lastPolled: new Date().toLocaleTimeString(),
        pollCount: (currentData.pollCount || 0) + 1
      };
      
      // Update the token table with new data
      updateTokenTable(tokenAddress, updatedData, 'POLL UPDATE');
      
      // Log poll results (only when bots are active)
      if (changes.length > 0) {
        console.log(`🔄 ACTIVE POLL: ${tokenInfo?.symbol || tokenAddress.slice(0, 8)} - ${changes.join(', ')} (bot monitoring)`);
      } else {
        console.log(`📊 ACTIVE POLL: ${tokenInfo?.symbol || tokenAddress.slice(0, 8)} - no changes (poll #${updatedData.pollCount}, bot monitoring)`);
      }
    } catch (error) {
      console.error(`Error polling token data for ${tokenAddress}:`, error);
    }
  };

  // Update token table with new data
  const updateTokenTable = (tokenAddress: string, updateData: any, updateType: string) => {
    const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
    const updatedData = { ...currentData, ...updateData };
    masterTokenDataRef.current.set(tokenAddress, updatedData);
    
    // Check auto trader rules for this token
    checkAutoTraderRules(tokenAddress, updatedData);
  };

  // Map field names to data fields
  const mapFieldToDataField = (field: LocalAutoTraderCondition['field']): string => {
    switch (field) {
      case 'marketCap': return 'marketCapUsd';
      case 'liquidity': return 'liquidityUsd';
      case 'holders': return 'holders';
      case 'buys': return 'buys';
      case 'sells': return 'sells';
      case 'curvePercentage': return 'curvePercentage';
      case 'txAmount': return 'tokensBought';
      case 'txVolume': return 'txVolume';
      case 'name': return 'name';
      case 'symbol': return 'symbol';
      default: return field;
    }
  };

  // Function to set baseline values for percentage calculations
  const setRuleBaseline = (ruleId: string, tokenAddress: string, fieldName: string, value: number) => {
    if (!ruleBaselinesRef.current.has(ruleId)) {
      ruleBaselinesRef.current.set(ruleId, new Map());
    }
    
    const ruleBaselines = ruleBaselinesRef.current.get(ruleId)!;
    if (!ruleBaselines.has(tokenAddress)) {
      ruleBaselines.set(tokenAddress, new Map());
    }
    
    const tokenBaselines = ruleBaselines.get(tokenAddress)!;
    if (!tokenBaselines.has(fieldName)) {
      tokenBaselines.set(fieldName, value);
      console.log(`📊 AUTO TRADER BASELINE SET: Rule "${ruleId}" - ${fieldName} baseline for ${tokenAddress.slice(0, 8)}... = ${value}`);
    }
  };

  // Function to get baseline value for percentage calculations
  const getRuleBaseline = (ruleId: string, tokenAddress: string, fieldName: string): number | null => {
    const ruleBaselines = ruleBaselinesRef.current.get(ruleId);
    if (!ruleBaselines) return null;
    
    const tokenBaselines = ruleBaselines.get(tokenAddress);
    if (!tokenBaselines) return null;
    
    return tokenBaselines.get(fieldName) || null;
  };

  // Function to calculate percentage change from baseline
  const calculatePercentageChange = (currentValue: number, baselineValue: number): number => {
    if (baselineValue === 0) return 0; // Avoid division by zero
    return ((currentValue - baselineValue) / baselineValue) * 100;
  };

  // Check auto trader rules against token data
  const checkAutoTraderRules = (tokenAddress: string, tokenData: any) => {
    const activeRules = autoTraderRulesRef.current.filter(rule => 
      rule.isActive && rule.tokenAddress === tokenAddress
    );
    
    if (activeRules.length === 0) return;

    activeRules.forEach(rule => {
      // First, set baselines for any percentage operators in this rule
      rule.conditions.forEach(condition => {
        if (condition.operator === 'increase%' || condition.operator === 'decrease%') {
          const dataField = mapFieldToDataField(condition.field);
          const fieldValue = tokenData[dataField];
          
          if (fieldValue !== undefined && fieldValue !== null && typeof fieldValue === 'number') {
            setRuleBaseline(rule.id, tokenAddress, dataField, fieldValue);
          }
        }
      });

      // Check if all conditions are met
      const allConditionsMet = rule.conditions.every(condition => {
        const dataField = mapFieldToDataField(condition.field);
        const actualValue = tokenData[dataField];
        
        if (actualValue === undefined || actualValue === null) return false;
        
        switch (condition.operator) {
          case '>': return actualValue > condition.value;
          case '<': return actualValue < condition.value;
          case '>=': return actualValue >= condition.value;
          case '<=': return actualValue <= condition.value;
          case '=': 
            if (condition.field === 'name' || condition.field === 'symbol') {
              return String(actualValue).toLowerCase().includes(String(condition.value).toLowerCase());
            }
            return actualValue === condition.value;
          case 'increase%': {
            const baseline = getRuleBaseline(rule.id, tokenAddress, dataField);
            if (baseline === null) {
              console.log(`📊 No baseline yet for ${dataField} in auto trader rule "${rule.name}" - skipping percentage check`);
              return false; // No baseline yet, can't calculate percentage
            }
            const percentageChange = calculatePercentageChange(actualValue, baseline);
            const result = percentageChange >= condition.value;
            console.log(`📈 AUTO TRADER INCREASE% CHECK: ${dataField} changed ${percentageChange.toFixed(2)}% (baseline: ${baseline}, current: ${actualValue}, target: +${condition.value}%) = ${result}`);
            return result;
          }
          case 'decrease%': {
            const baseline = getRuleBaseline(rule.id, tokenAddress, dataField);
            if (baseline === null) {
              console.log(`📊 No baseline yet for ${dataField} in auto trader rule "${rule.name}" - skipping percentage check`);
              return false; // No baseline yet, can't calculate percentage
            }
            const percentageChange = calculatePercentageChange(actualValue, baseline);
            const result = Math.abs(percentageChange) >= condition.value && percentageChange < 0;
            console.log(`📉 AUTO TRADER DECREASE% CHECK: ${dataField} changed ${percentageChange.toFixed(2)}% (baseline: ${baseline}, current: ${actualValue}, target: -${condition.value}%) = ${result}`);
            return result;
          }
          default: return false;
        }
      });

      if (allConditionsMet) {
        const tokenInfo = tokenMetadataRef.current.get(tokenAddress);
        executeAutoTraderTrade(
          rule.id,
          tokenAddress,
          tokenInfo || null,
          {
            solAmountPerTrade: rule.solAmountPerTrade || parseFloat(solAmountPerTrade),
            slippagePercent: rule.slippagePercent || parseFloat(slippagePercent),
            maxTradesToExecute: rule.maxTradesToExecute || parseFloat(maxTradesToExecute),
            tradingWalletAddresses: rule.tradingWalletAddresses || Array.from(selectedWallets),
            tradeType: rule.tradeType || 'buy'
          },
          rule.name,
          rule.conditions
        );
      }
    });
  };

  // Execute auto trader trade
  const executeAutoTraderTrade = async (
    ruleId: string,
    tokenAddress: string,
    tokenInfo: { name: string; symbol: string } | null,
    ruleConfig: {
      solAmountPerTrade: number;
      slippagePercent: number;
      maxTradesToExecute: number;
      tradingWalletAddresses: string[];
      tradeType: 'buy' | 'sell';
    },
    appliedRuleName: string,
    matchedConditions?: LocalAutoTraderCondition[]
  ) => {
    // Prevent duplicate trades
    const now = Date.now();
    const lastTradeTime = recentlyTradedTokensRef.current.get(tokenAddress);
    if (lastTradeTime && (now - lastTradeTime) < DUPLICATE_PREVENTION_TIME) {
      console.log(`⏭️ Skipping duplicate trade for ${tokenInfo?.symbol || tokenAddress} (last trade ${Math.round((now - lastTradeTime) / 1000)}s ago)`);
      return;
    }

    // Check if we've hit the max trades for this rule
    const currentTradeCount = ruleTradeCountRef.current.get(ruleId) || 0;
    if (currentTradeCount >= ruleConfig.maxTradesToExecute) {
      console.log(`🛑 Max trades reached for rule "${appliedRuleName}" (${currentTradeCount}/${ruleConfig.maxTradesToExecute})`);
      return;
    }

    // Prevent race conditions
    if (ruleExecutionLockRef.current.has(ruleId)) {
      console.log(`⏳ Trade already executing for rule "${appliedRuleName}", skipping...`);
      return;
    }

    const executePromise = (async () => {
      try {
        // Mark this token as recently traded
        recentlyTradedTokensRef.current.set(tokenAddress, now);

        // Get wallet for this trade (round-robin)
        const walletIndex = walletRotationRef.current.get(ruleId) || 0;
        const selectedWalletAddress = ruleConfig.tradingWalletAddresses[walletIndex % ruleConfig.tradingWalletAddresses.length];
        
        // Update wallet rotation for next trade
        walletRotationRef.current.set(ruleId, walletIndex + 1);

        console.log(`🎯 AUTO TRADER TRIGGERED: "${appliedRuleName}" for ${tokenInfo?.symbol || tokenAddress}`);
        console.log(`💰 Trade: ${ruleConfig.tradeType} ${ruleConfig.solAmountPerTrade} SOL at ${ruleConfig.slippagePercent}% slippage`);
        console.log(`🏦 Wallet: ${selectedWalletAddress}`);

        // Execute the trade based on type FIRST
        let result;
        if (ruleConfig.tradeType === 'buy') {
          result = await tradeEngine.executeSwap({
            inputMint: SOL_MINT,
            outputMint: tokenAddress,
            amount: ruleConfig.solAmountPerTrade.toString(),
            userWalletAddress: getEffectiveWalletAddress()!,
            tradingWalletAddress: selectedWalletAddress,
            slippagePercent: ruleConfig.slippagePercent.toString()
          });
        } else {
          // For sell trades, we need to get the token balance first
          const { getTokenBalance } = await import('../../services/solanatrackerService');
          const tokenBalance = await getTokenBalance(selectedWalletAddress, tokenAddress);
          
          result = await tradeEngine.executeSwap({
            inputMint: tokenAddress,
            outputMint: SOL_MINT,
            amount: tokenBalance, // Sell all available tokens
            userWalletAddress: getEffectiveWalletAddress()!,
            tradingWalletAddress: selectedWalletAddress,
            slippagePercent: ruleConfig.slippagePercent.toString()
          });
        }

        // ONLY show loading notification AFTER we've successfully started the transaction
        // This prevents notifications for pre-transaction failures like "no route available"
        showAutoTraderNotification(
          'loading',
          appliedRuleName,
          tokenInfo?.symbol || 'Unknown',
          tokenInfo?.name || 'Unknown Token',
          ruleConfig.solAmountPerTrade,
          selectedWalletAddress,
          ruleConfig.tradeType,
          undefined,
          undefined,
          matchedConditions
        );

        if (result.signature) {
          // Increment trade count
          ruleTradeCountRef.current.set(ruleId, currentTradeCount + 1);

          console.log(`✅ AUTO TRADER SUCCESS: ${result.signature}`);
          
                  // Record successful trade
        await autoTraderRulesService.recordAutoTraderTrade(
          getEffectiveWalletAddress()!,
          ruleId,
          tokenAddress,
          tokenInfo?.name || 'Unknown Token',
          tokenInfo?.symbol || 'Unknown',
          selectedWalletAddress,
          ruleConfig.solAmountPerTrade,
          ruleConfig.slippagePercent,
          ruleConfig.tradeType,
          result.signature,
          'success',
          undefined,
          matchedConditions
        );

          // Show success notification
          showAutoTraderNotification(
            'success',
            appliedRuleName,
            tokenInfo?.symbol || 'Unknown',
            tokenInfo?.name || 'Unknown Token',
            ruleConfig.solAmountPerTrade,
            selectedWalletAddress,
            ruleConfig.tradeType,
            result.signature,
            undefined,
            matchedConditions
          );

        } else {
          console.error(`❌ AUTO TRADER FAILED:`, result);
          
          // Record failed trade
          await autoTraderRulesService.recordAutoTraderTrade(
            getEffectiveWalletAddress()!,
            ruleId,
            tokenAddress,
            tokenInfo?.name || 'Unknown Token',
            tokenInfo?.symbol || 'Unknown',
            selectedWalletAddress,
            ruleConfig.solAmountPerTrade,
            ruleConfig.slippagePercent,
            ruleConfig.tradeType,
            undefined,
            'failed',
            'Trade execution failed',
            matchedConditions
          );

          // No error notification for failed transactions
        }

      } catch (error) {
        console.error(`❌ AUTO TRADER ERROR:`, error);
        
        // Get wallet for error recording
        const walletIndex = walletRotationRef.current.get(ruleId) || 0;
        const selectedWalletAddress = ruleConfig.tradingWalletAddresses[walletIndex % ruleConfig.tradingWalletAddresses.length];
        
        // Record failed trade
        await autoTraderRulesService.recordAutoTraderTrade(
          getEffectiveWalletAddress()!,
          ruleId,
          tokenAddress,
          tokenInfo?.name || 'Unknown Token',
          tokenInfo?.symbol || 'Unknown',
          selectedWalletAddress,
          ruleConfig.solAmountPerTrade,
          ruleConfig.slippagePercent,
          ruleConfig.tradeType,
          undefined,
          'failed',
          error instanceof Error ? error.message : 'Unknown error',
          matchedConditions
        );

        // No error notification for failed transactions
      }
    })();

    ruleExecutionLockRef.current.set(ruleId, executePromise);
    
    try {
      await executePromise;
    } finally {
      ruleExecutionLockRef.current.delete(ruleId);
    }
  };

  // Show auto trader notification
  function showAutoTraderNotification(
    type: 'loading' | 'success' | 'error',
    botName: string,
    tokenSymbol: string,
    tokenName: string,
    amount: number,
    walletAddress: string,
    tradeType: 'buy' | 'sell',
    txSignature?: string,
    errorMessage?: string,
    matchedConditions?: LocalAutoTraderCondition[]
  ) {
    const shortWallet = `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`;
    
    if (type === 'loading') {
      showNotification(
        `🤖 Auto Trader "${botName}" executing ${tradeType}: ${amount} SOL ${tradeType === 'buy' ? '→' : '←'} ${tokenSymbol} via ${shortWallet}`,
        'loading'
      );
    } else if (type === 'success') {
      showNotification(
        `✅ Auto Trader "${botName}" successfully ${tradeType === 'buy' ? 'bought' : 'sold'} ${tokenSymbol} (${amount} SOL) ${txSignature ? `- TX: ${txSignature.slice(0, 8)}...` : ''}`,
        'success'
      );
    } else if (type === 'error') {
      // No longer show error notifications for failed transactions
      // Only log to console for debugging
      console.log(`❌ Auto Trader "${botName}" failed to ${tradeType} ${tokenSymbol}: ${errorMessage || 'Unknown error'}`);
    }
  }

  // Load trading wallets
  const loadTradingWallets = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;
    
    setIsLoadingWallets(true);
    try {
      // FIXED: Use built-in parallel balance loading for much better performance
      console.log(`💼 Loading trading wallets with balances in parallel...`);
      const walletsWithBalances = await getUserTradingWallets(effectiveWalletAddress, { 
        includeBalances: true  // ← This does parallel loading internally
      });
      
      setTradingWallets(walletsWithBalances);
      
      // Extract balances from the wallets data 
      const balances = new Map<string, number>();
      walletsWithBalances.forEach(wallet => {
        if (wallet.balance !== undefined) {
          balances.set(wallet.public_key, wallet.balance);
        } else {
          balances.set(wallet.public_key, 0);
        }
      });
      setWalletBalances(balances);
      
      console.log(`✅ Loaded ${walletsWithBalances.length} trading wallets with balances in parallel (much faster!)`);
      
    } catch (error) {
      console.error('Error loading trading wallets:', error);
      
      // Fallback: Load wallets without balances if parallel loading fails
      try {
        console.log(`🔄 Falling back to loading wallets without balances...`);
        const wallets = await getUserTradingWallets(effectiveWalletAddress);
        setTradingWallets(wallets);
        
        // Set all balances to 0 as fallback
        const balances = new Map<string, number>();
        wallets.forEach(wallet => {
          balances.set(wallet.public_key, 0);
        });
        setWalletBalances(balances);
        
        console.log(`⚠️ Loaded ${wallets.length} trading wallets without balances (fallback mode)`);
      } catch (fallbackError) {
        console.error('Error in fallback wallet loading:', fallbackError);
      }
    } finally {
      setIsLoadingWallets(false);
    }
  };

  // Refresh trading wallet balances (exactly matching SniperBot)
  const refreshTradingWalletBalances = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress || tradingWallets.length === 0) return;
    
    console.log(`🔄 Refreshing trading wallet balances for ${tradingWallets.length} wallets...`);
    
    try {
      // Refresh balances in parallel using Promise.all for speed
      const balancePromises = tradingWallets.map(async (wallet) => {
        try {
          const balanceInLamports = await getBalance(wallet.public_key);
          return {
            address: wallet.public_key,
            balance: balanceInLamports / 1_000_000_000 // Convert to SOL
          };
        } catch (error) {
          console.error(`Error refreshing balance for wallet ${wallet.public_key}:`, error);
          return {
            address: wallet.public_key,
            balance: walletBalances.get(wallet.public_key) || 0 // Keep existing balance on error
          };
        }
      });

      const balanceResults = await Promise.all(balancePromises);
      
      // Update the balance map
      const newBalances = new Map<string, number>();
      balanceResults.forEach(result => {
        newBalances.set(result.address, result.balance);
      });
      
      setWalletBalances(newBalances);
      console.log(`✅ Refreshed balances for ${balanceResults.length} trading wallets`);
      
    } catch (error) {
      console.error('Error refreshing trading wallet balances:', error);
    }
  };

  // Load auto trader rules
  const loadAutoTraderRules = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;
    
    setIsLoadingRules(true);
    try {
      // Use the dedicated auto trader rules service
      const rules = await autoTraderRulesService.getUserAutoTraderRules(effectiveWalletAddress);
      
      // Convert to local format
      const localRules: LocalAutoTraderRule[] = rules.map((rule: AutoTraderRule) => ({
        id: rule.id,
        name: rule.name,
        isActive: rule.is_active,
        conditions: rule.conditions.map((c: AutoTraderCondition) => ({
          field: c.field as LocalAutoTraderCondition['field'],
          operator: c.operator,
          value: c.value
        })),
        createdAt: rule.created_at,
        tokenAddress: rule.token_address,
        tokenSymbol: rule.token_symbol,
        tokenName: rule.token_name,
        tradeType: rule.trade_type,
        solAmountPerTrade: rule.sol_amount_per_trade,
        maxTradesToExecute: rule.max_trades_to_execute,
        slippagePercent: rule.slippage_percent,
        tradingWalletAddresses: rule.trading_wallet_addresses,
        totalSolBudget: rule.total_sol_budget,
        configurationMetadata: rule.configuration_metadata,
      }));
      
      setAutoTraderRules(localRules);
      autoTraderRulesRef.current = localRules;
      
      // Initialize trading state on first load if there are active rules
      const hasActiveRules = localRules.some(rule => rule.isActive);
      const currentTradingState = isTradingActive;
      
      // Only update state if it's different to avoid unnecessary updates
      if (hasActiveRules !== currentTradingState) {
        console.log(`🔄 INITIALIZING TRADING STATE: ${currentTradingState} → ${hasActiveRules}`);
        setIsTradingActive(hasActiveRules);
        isTradingActiveRef.current = hasActiveRules;
      }
      
      const activeCount = localRules.filter(r => r.isActive).length;
      if (activeCount > 0) {
        console.log(`🎯 AUTO TRADER RULES LOADED: ${activeCount} active rule(s)`);
        
        // Subscribe to websockets AND start polling for active tokens only
        localRules.filter(r => r.isActive).forEach(rule => {
          if (rule.tokenAddress && rule.tokenSymbol) {
            subscribeToTokenRooms(rule.tokenAddress, { 
              name: rule.tokenName || rule.tokenSymbol, 
              symbol: rule.tokenSymbol 
            });
          }
        });
      } else {
        console.log(`⏹️ AUTO TRADER RULES LOADED: No active rules - no polling or websockets started`);
      }
    } catch (error) {
      console.error('Error loading auto trader rules:', error);
    } finally {
      setIsLoadingRules(false);
    }
  };

  // Subscribe to token rooms for websocket updates AND start polling (only when bots are active)
  const subscribeToTokenRooms = (tokenAddress: string, tokenInfo: { name: string; symbol: string }) => {
    if (subscribedTokensRef.current.has(tokenAddress)) {
      console.log(`⏭️ Already subscribed to ${tokenInfo.symbol} (${tokenAddress}) - websockets and polling already active`);
      return;
    }

    console.log(`🔗 STARTING MONITORING: ${tokenInfo.symbol} (${tokenAddress}) - websockets + polling`);
    
    // Store token metadata
    tokenMetadataRef.current.set(tokenAddress, tokenInfo);
    
    // Add to subscribed tokens set
    subscribedTokensRef.current.add(tokenAddress);
    
    const unsubscribeFunctions: (() => void)[] = [];

    try {
      // 1. Transactions - Real-time buy/sell data
      wsService.joinRoom(`transaction:${tokenAddress}`);
      const unsubTransaction = wsService.on(`transaction:${tokenAddress}`, (data) => {
        if (Array.isArray(data)) {
          data.forEach(tx => {
            // Get current data to update buy/sell counters
            const currentData = masterTokenDataRef.current.get(tokenAddress) || {};
            
            const txData = {
              lastTxType: tx.type?.toUpperCase(),
              lastTxAmount: tx.amount,
              lastTxVolume: tx.volume,
              priceUsd: tx.priceUsd,
              lastTxWallet: tx.wallet?.slice(0, 8) + '...',
              lastTxProgram: tx.program,
              lastTxTime: new Date().toLocaleTimeString(),
              // Increment buy or sell counter based on transaction type
              buys: tx.type?.toLowerCase() === 'buy' ? (currentData.buys || 0) + 1 : (currentData.buys || 0),
              sells: tx.type?.toLowerCase() === 'sell' ? (currentData.sells || 0) + 1 : (currentData.sells || 0),
              // Track tokens bought (only for buy transactions)
              tokensBought: tx.type?.toLowerCase() === 'buy' ? tx.amount : (currentData.tokensBought || 0)
            };
            updateTokenTable(tokenAddress, txData, `TRANSACTION: ${tx.type?.toUpperCase()}`);
          });
        }
      });
      unsubscribeFunctions.push(unsubTransaction);

      // 2. Holders update - Real-time holder count
      wsService.joinRoom(`holders:${tokenAddress}`);
      const unsubHolders = wsService.on(`holders:${tokenAddress}`, (data) => {
        if (data && data.total !== undefined) {
          const holdersData = {
            holders: data.total
          };
          updateTokenTable(tokenAddress, holdersData, 'HOLDERS UPDATE');
        }
      });
      unsubscribeFunctions.push(unsubHolders);

      // 3. Token Changes - Real-time price, market cap, liquidity
      wsService.joinRoom(`token:${tokenAddress}`);
      const unsubToken = wsService.on(`token:${tokenAddress}`, (data) => {
        if (data && data.price && data.marketCap && data.liquidity) {
          const tokenData = {
            priceUsd: data.price.usd,
            marketCapUsd: data.marketCap.usd,
            liquidityUsd: data.liquidity.usd,
            lpBurn: data.lpBurn,
            market: data.market,
            tokenSupply: data.tokenSupply,
            curvePercentage: data.curvePercentage,
            lastUpdated: new Date(data.lastUpdated).toLocaleTimeString()
          };
          updateTokenTable(tokenAddress, tokenData, 'TOKEN UPDATE');
        }
      });
      unsubscribeFunctions.push(unsubToken);

      // 4. Backup polling (every 5 seconds) - ONLY ACTIVE WHEN BOTS ARE RUNNING
      const pollInterval = setInterval(() => {
        if (subscribedTokensRef.current.has(tokenAddress)) {
          pollTokenData(tokenAddress);
        } else {
          clearInterval(pollInterval);
        }
      }, 5000); // Poll every 5 seconds for more frequent updates

      unsubscribeFunctions.push(() => {
        clearInterval(pollInterval);
        console.log(`🔌 STOPPED POLLING: ${tokenInfo.symbol} (no active bots)`);
      });

      // Store unsubscribe functions for cleanup
      tokenSubscriptionsRef.current.set(tokenAddress, unsubscribeFunctions);

      console.log(`✅ ACTIVE MONITORING: ${tokenInfo.symbol} - websockets + 30s polling started`);
      
    } catch (error) {
      console.error(`❌ Error subscribing to ${tokenInfo.symbol}:`, error);
    }
  };

  // Handle rule toggle
  const handleRuleToggle = async (ruleId: string) => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    const rule = autoTraderRules.find(r => r.id === ruleId);
    if (!rule) return;

    try {
      const success = await autoTraderRulesService.toggleRuleActive(effectiveWalletAddress, ruleId);
      
      if (success) {
        // Reload rules to get updated state
        await loadAutoTraderRules();
        await loadRuleStatistics();
        
        // Get the updated rules and properly set the trading active state
        const updatedRules = await autoTraderRulesService.getUserAutoTraderRules(effectiveWalletAddress);
        const hasActiveRules = updatedRules.some(rule => rule.is_active);
        
        // Update trading state
        setIsTradingActive(hasActiveRules);
        isTradingActiveRef.current = hasActiveRules;
        
        // Show notification for bot start/stop
        const toggledRule = updatedRules.find(r => r.id === ruleId);
        if (toggledRule) {
                  if (toggledRule.is_active) {
          // Bot started - clear any existing baselines to start fresh
          ruleBaselinesRef.current.delete(toggledRule.id);
          console.log(`🔄 Cleared baselines for auto trader rule "${toggledRule.name}" - percentage operators will establish new baselines`);
          
          // Start websockets AND polling for this token
          if (toggledRule.token_address && toggledRule.token_symbol) {
            subscribeToTokenRooms(toggledRule.token_address, { 
              name: toggledRule.token_name || toggledRule.token_symbol, 
              symbol: toggledRule.token_symbol 
            });
          }
          showNotification(
            `Auto Trader Bot "${toggledRule.name}" started! Now monitoring ${toggledRule.token_symbol} with real-time data.`,
            'success'
          );
          console.log(`🎯 BOT STARTED: "${toggledRule.name}" - websockets + polling active for ${toggledRule.token_symbol}`);
          } else {
            // Bot stopped - stop websockets AND polling for this token if no other active bots use it
            const otherActiveBotsForToken = updatedRules.filter(r => 
              r.is_active && r.token_address === toggledRule.token_address && r.id !== ruleId
            );
            
            if (otherActiveBotsForToken.length === 0 && toggledRule.token_address) {
              // No other active bots for this token, stop all monitoring
              const cleanupFunctions = tokenSubscriptionsRef.current.get(toggledRule.token_address);
              if (cleanupFunctions) {
                cleanupFunctions.forEach(cleanup => cleanup());
                tokenSubscriptionsRef.current.delete(toggledRule.token_address);
                subscribedTokensRef.current.delete(toggledRule.token_address);
                console.log(`🔌 STOPPED MONITORING: ${toggledRule.token_symbol} - websockets + polling stopped (no active bots)`);
              }
            }
            
            showNotification(
              `Auto Trader Bot "${toggledRule.name}" stopped. No longer monitoring ${toggledRule.token_symbol}.`,
              'success'
            );
            console.log(`⏹️ BOT STOPPED: "${toggledRule.name}" - monitoring paused`);
          }
        }
        
        if (hasActiveRules) {
          console.log(`🎯 AUTO TRADER SYSTEM STATUS: ${updatedRules.filter(r => r.is_active).length} active bot(s)`);
        } else {
          console.log(`⏹️ AUTO TRADER SYSTEM: All bots stopped`);
        }
      }
    } catch (error) {
      console.error('Error toggling rule:', error);
      showNotification('Failed to toggle bot status', 'error');
    }
  };

  // Load rule statistics
  const loadRuleStatistics = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    try {
      const tradeHistory = await autoTraderRulesService.getUserTradeHistory(effectiveWalletAddress, 1000);
      
      // Calculate statistics per rule
      const stats = new Map<string, {triggers: number, success: number, failed: number}>();
      
      tradeHistory.forEach((trade: any) => {
        if (!trade.rule_id) return; // Skip trades without rule_id
        
        const ruleStats = stats.get(trade.rule_id) || {triggers: 0, success: 0, failed: 0};
        
        ruleStats.triggers++;
        if (trade.status === 'success') {
          ruleStats.success++;
        } else if (trade.status === 'failed') {
          ruleStats.failed++;
        }
        
        stats.set(trade.rule_id, ruleStats);
      });
      
      setRuleStatistics(stats);
    } catch (error) {
      console.error('Error loading rule statistics:', error);
    }
  };

  // Load trade history
  const loadTradeHistory = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    setIsLoadingTradeHistory(true);
    try {
      const history = await autoTraderRulesService.getUserTradeHistory(effectiveWalletAddress, 100);
      setTradeHistory(history);
    } catch (error) {
      console.error('Error loading trade history:', error);
    } finally {
      setIsLoadingTradeHistory(false);
    }
  };

  // Setup realtime subscription for trade history
  const setupRealtimeSubscription = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress) return;

    try {
      const user = await userService.getUserByWallet(effectiveWalletAddress);
      if (!user) {
        console.error('User not found for realtime subscription');
        return;
      }

      // Clean up existing subscription
      if (realtimeSubscriptionRef.current) {
        await adminSupabase.removeChannel(realtimeSubscriptionRef.current);
        realtimeSubscriptionRef.current = null;
      }

      // Setup realtime subscription to auto_trader_trade_history table
      realtimeSubscriptionRef.current = adminSupabase
        .channel(`auto_trader_trade_history_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'auto_trader_trade_history',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('📡 Realtime auto trader trade history update:', payload.eventType, payload.new || payload.old);

            if (payload.eventType === 'INSERT' && payload.new) {
              // Add new trade to history at the top (most recent first)
              setTradeHistory(prev => {
                // Check if trade already exists to prevent duplicates
                const exists = prev.some(trade => trade.id === payload.new.id);
                if (exists) {
                  console.log('📡 Trade already exists in history, skipping duplicate');
                  return prev;
                }
                
                console.log(`📡 Adding new trade to history: ${payload.new.token_symbol} - ${payload.new.status}`);
                return [payload.new, ...prev];
              });

              // Update rule statistics
              loadRuleStatistics();
            } else if (payload.eventType === 'UPDATE' && payload.new) {
              // Update existing trade
              setTradeHistory(prev =>
                prev.map(trade =>
                  trade.id === payload.new.id ? payload.new : trade
                )
              );

              // Update rule statistics
              loadRuleStatistics();
            } else if (payload.eventType === 'DELETE' && payload.old) {
              // Remove trade from history
              setTradeHistory(prev => {
                const filtered = prev.filter(trade => trade.id !== payload.old.id);
                console.log(`📡 Removed trade ${payload.old.id} from history`);
                return filtered;
              });

              // Update rule statistics
              loadRuleStatistics();
            }
          }
        )
        .subscribe();

      console.log('✅ Successfully subscribed to auto trader trade history realtime updates');
    } catch (error) {
      console.error('Error setting up realtime subscription:', error);
    }
  };

  // Calculate total SOL needed for trading
  const calculateTotalSolNeeded = () => {
    const solPerTrade = parseFloat(solAmountPerTrade) || 0;
    const maxTrades = parseFloat(maxTradesToExecute) || 0;
    return solPerTrade * maxTrades * selectedWallets.size;
  };

  // Get total available balance across selected wallets
  const getTotalAvailableBalance = () => {
    return Array.from(selectedWallets).reduce((total, walletAddress) => {
      return total + (walletBalances.get(walletAddress) || 0);
    }, 0);
  };

  // Toggle wallet selection
  const toggleWalletSelection = (walletAddress: string) => {
    const newSelected = new Set(selectedWallets);
    if (newSelected.has(walletAddress)) {
      newSelected.delete(walletAddress);
    } else {
      newSelected.add(walletAddress);
    }
    setSelectedWallets(newSelected);
  };

  // Render wallet selection (exactly matching SniperBot)
  const renderWalletSelection = () => (
    <div className="mb-1.5">
      <h4 className="text-black text-xs font-medium mb-1">Trading Wallets</h4>
      {isLoadingWallets ? (
        <div className="flex items-center justify-center py-2">
          <Loader size={12} className="text-[#24c35b] animate-spin mr-1" />
          <span className="text-gray-400 text-xs">Loading...</span>
        </div>
      ) : tradingWallets.length === 0 ? (
        <div className="text-center py-2">
          <p className="text-gray-400 text-xs">No wallets found</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-28 overflow-y-auto">
          {tradingWallets.map(wallet => {
            const balance = walletBalances.get(wallet.public_key) || 0;
            const isSelected = selectedWallets.has(wallet.public_key);
            
            return (
              <div
                key={wallet.public_key}
                className={`p-1 rounded border cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-[#24c35b] bg-[#24c35b]/10' 
                    : 'border-gray-200 bg-white hover:border-[#24c35b]/40'
                }`}
                onClick={() => toggleWalletSelection(wallet.public_key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded border mr-1.5 flex items-center justify-center ${
                      isSelected ? 'border-[#24c35b] bg-[#24c35b]' : 'border-gray-500'
                    }`}>
                      {isSelected && (
                        <div className="w-1.5 h-1.5 bg-white rounded-sm"></div>
                      )}
                    </div>
                    <div>
                      <p className="text-black text-xs font-medium">{wallet.name}</p>
                      <p className="text-gray-500 text-xs font-mono">
                        {wallet.public_key.slice(0, 6)}...{wallet.public_key.slice(-4)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-black">
                      {balance.toFixed(3)} SOL
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Render token selection step
  const renderTokenSelection = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-white text-base font-semibold mb-0.5">
          {editingBotId ? 'Edit Auto Trader Bot' : 'Create Auto Trader Bot'}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Modify token and trade type' : 'Select token and trade type for automated trading'}
        </p>
      </div>

      {/* Token Selection */}
      <div>
        <label className="text-white text-xs font-medium mb-1 block">
          Select Token
        </label>
        
        {selectedToken ? (
          <div className="p-2 rounded border border-[#24c35b]/30 bg-[#24c35b]/5">
            <div className="flex items-center gap-2 min-w-0">
              {selectedToken.image_uri && (
                <img
                  src={selectedToken.image_uri}
                  alt={selectedToken.symbol || 'Token'}
                  className="w-6 h-6 rounded-full flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-medium">{selectedToken.symbol}</div>
                <div className="text-gray-400 text-xs truncate">{selectedToken.name}</div>
                <div className="text-gray-500 text-xs font-mono truncate">{selectedToken.mint.slice(0, 8)}...{selectedToken.mint.slice(-8)}</div>
              </div>
              <button
                onClick={() => setSelectedToken(null)}
                className="text-gray-400 hover:text-red-400 transition-colors flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ) : (
          <button
            ref={searchButtonRef}
            onClick={() => setIsSearchModalOpen(true)}
            className="w-full p-2 rounded border border-gray-600/20 bg-gray-600/5 hover:bg-gray-600/10 transition-all text-left flex items-center gap-2"
          >
            <Search size={14} className="text-gray-400" />
            <span className="text-gray-400 text-xs">Click to search for a token...</span>
          </button>
        )}
      </div>

      {/* Trade Type Selection */}
      <div>
        <label className="text-white text-xs font-medium mb-1 block">
          Trade Type
        </label>
        <div className="grid grid-cols-2 gap-1">
          <button
            onClick={() => setTradeType('buy')}
            className={`p-2 rounded border text-xs font-medium transition-all ${
              tradeType === 'buy'
                ? 'border-[#24c35b] bg-[#24c35b]/20 text-[#24c35b]'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-[#24c35b]/40 hover:text-white'
            }`}
          >
            Buy Token
          </button>
          <button
            onClick={() => setTradeType('sell')}
            className={`p-2 rounded border text-xs font-medium transition-all ${
              tradeType === 'sell'
                ? 'border-red-500/40 bg-red-500/20 text-red-400'
                : 'border-gray-600/20 bg-gray-600/5 text-gray-400 hover:border-red-500/40 hover:text-red-400'
            }`}
          >
            Sell Token
          </button>
        </div>
        <div className="mt-1 text-[10px] text-gray-500">
          {tradeType === 'buy' 
            ? 'Bot will buy this token when conditions are met'
            : 'Bot will sell this token when conditions are met'
          }
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => {
            setIsCreatingBot(false);
            setNewBotName('');
            setNewBotRules([]);
            setEditingBotId(null);
            setSelectedToken(null);
            setTradeType('buy');
          }}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Cancel
        </button>
        <button
          onClick={() => setCreationStep('rules')}
          disabled={!selectedToken}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          Next: Rules
        </button>
      </div>
    </div>
  );

  // Render rules creation step (similar to SniperBot)
  const renderRulesCreation = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-white text-base font-semibold mb-0.5">
          {editingBotId ? 'Edit Trading Rules' : 'Create Trading Rules'}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Modify rules for your bot' : 'Set up rules for automatic trading'}
        </p>
      </div>

      {/* Selected Token Display */}
      {selectedToken && (
        <div className="p-2 rounded border border-gray-600/20 bg-gray-600/5 mb-2">
          <div className="text-xs text-gray-400 mb-1">Trading Token:</div>
          <div className="flex items-center gap-2 min-w-0">
            {selectedToken.image_uri && (
              <img
                src={selectedToken.image_uri}
                alt={selectedToken.symbol || 'Token'}
                className="w-4 h-4 rounded-full flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white text-xs font-medium">{selectedToken.symbol}</span>
                <span className="text-gray-400 text-xs truncate">({selectedToken.name})</span>
              </div>
            </div>
            <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
              tradeType === 'buy' 
                ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                : 'bg-red-500/20 text-red-400 border border-red-500/40'
            }`}>
              {tradeType.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Bot Name */}
      <div>
        <label className="text-white text-xs font-medium mb-1 block">
          Bot Name
        </label>
        <input
          type="text"
          value={newBotName}
          onChange={(e) => setNewBotName(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
          placeholder="e.g., EKKO Buy Bot"
        />
      </div>

      {/* Rules Configuration */}
      <div>
        <label className="text-white text-xs font-medium mb-1 block">
          Trading Rules
        </label>
        <div className="space-y-1">
          {newBotRules.map((rule, index) => (
            <div key={index} className="p-1 rounded border border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-1">
                <span className="text-black text-xs font-medium">
                  Rule {index + 1}
                </span>
                <button
                  onClick={() => {
                    const updatedRules = newBotRules.filter((_, i) => i !== index);
                    setNewBotRules(updatedRules);
                  }}
                  className="text-gray-500 hover:text-red-400 transition-colors"
                >
                  <Trash2 size={10} />
                </button>
              </div>
              
              <div className="space-y-1">
                <div className="grid grid-cols-3 gap-1">
                  <GlobalDropdown
                    options={Object.entries(AUTO_TRADER_FIELD_LABELS).map(([value, label]) => ({
                      value,
                      label: label as string
                    }))}
                    value={rule.field}
                    onChange={(value) => {
                      const updatedRules = [...newBotRules];
                      const currentRule = rule;
                      
                      // Auto-set operator to '=' for name and symbol fields
                      if (value === 'name' || value === 'symbol') {
                        updatedRules[index] = { 
                          ...currentRule, 
                          field: value as LocalAutoTraderCondition['field'],
                          operator: '='
                        };
                      } else {
                        updatedRules[index] = { ...currentRule, field: value as LocalAutoTraderCondition['field'] };
                      }
                      
                      setNewBotRules(updatedRules);
                    }}
                    placeholder="Field"
                  />
                  
                  <GlobalDropdown
                    options={[
                      { value: '>', label: '>', description: 'greater than' },
                      { value: '<', label: '<', description: 'less than' },
                      { value: '>=', label: '>=', description: 'greater/equal' },
                      { value: '<=', label: '<=', description: 'less/equal' },
                      { value: '=', label: '=', description: rule.field === 'name' || rule.field === 'symbol' ? 'contains text' : 'equals exactly' },
                      { value: 'increase%', label: '+%', description: 'increased by %' },
                      { value: 'decrease%', label: '-%', description: 'decreased by %' }
                    ].filter(option => {
                      // Only show '=' operator for name and symbol fields
                      if ((rule.field === 'name' || rule.field === 'symbol') && option.value !== '=') {
                        return false;
                      }
                      // Hide percentage operators for certain fields (existing logic)
                      if ((option.value === 'increase%' || option.value === 'decrease%') && 
                          !['marketCap', 'liquidity', 'holders', 'txVolume'].includes(rule.field)) {
                        return false;
                      }
                      return true;
                    })}
                    value={rule.operator}
                    onChange={(value) => {
                      const updatedRules = [...newBotRules];
                      updatedRules[index] = { ...rule, operator: value as LocalAutoTraderCondition['operator'] };
                      setNewBotRules(updatedRules);
                    }}
                    placeholder="Op"
                  />
                  
                  {(rule.field === 'name' || rule.field === 'symbol') ? (
                    <input
                      type="text"
                      value={String(rule.value || '')}
                      onChange={(e) => {
                        const updatedRules = [...newBotRules];
                        updatedRules[index] = { ...rule, value: e.target.value as any };
                        setNewBotRules(updatedRules);
                      }}
                      className="bg-white border border-gray-200 rounded px-1.5 py-1 text-black text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50"
                      placeholder="Token name or symbol"
                    />
                  ) : (
                    <input
                      type="number"
                      value={rule.value}
                      onChange={(e) => {
                        const updatedRules = [...newBotRules];
                        updatedRules[index] = { ...rule, value: parseFloat(e.target.value) || 0 };
                        setNewBotRules(updatedRules);
                      }}
                      className="bg-white border border-gray-200 rounded px-1.5 py-1 text-black text-xs hover:border-[#24c35b]/40 focus:border-[#24c35b]/50"
                      placeholder="Value"
                    />
                  )}
                </div>
                

              </div>
            </div>
          ))}
          
          <button
            onClick={() => {
              setNewBotRules([...newBotRules, {
                field: 'marketCap',
                operator: '>',
                value: 100000
              }]);
            }}
            className="w-full p-1.5 border border-dashed border-gray-600/50 rounded text-gray-400 hover:text-white hover:border-[#24c35b]/50 transition-all text-xs"
          >
            <Plus size={10} className="mr-1 inline" />
            Add Rule
          </button>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setCreationStep('token')}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Back: Token
        </button>
        <button
          onClick={() => setCreationStep('config')}
          disabled={!newBotName.trim() || newBotRules.length === 0}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          Next: Configure
        </button>
      </div>
    </div>
  );

  // Render config creation step (similar to SniperBot)
  const renderConfigCreation = () => (
    <div className="space-y-1.5">
      <div className="text-center mb-1.5">
        <h3 className="text-white text-base font-semibold mb-0.5">
          {editingBotId ? `Update Bot: ${newBotName}` : `Configure Bot: ${newBotName}`}
        </h3>
        <p className="text-gray-500 text-xs">
          {editingBotId ? 'Update wallet and amount settings' : 'Select wallets and amounts'}
        </p>
      </div>

      {/* Reuse existing configuration components */}
      {renderWalletSelection()}
      
      {/* Trading Configuration in Grid */}
      <div className="grid grid-cols-3 gap-2 mb-1.5">
        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            SOL per Trade
          </label>
          <input
            type="number"
            value={solAmountPerTrade}
            onChange={(e) => setSolAmountPerTrade(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="0.1"
            step="0.01"
            min="0"
          />
        </div>

        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Max Trades
          </label>
          <input
            type="number"
            value={maxTradesToExecute}
            onChange={(e) => setMaxTradesToExecute(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="5"
            min="1"
          />
        </div>

        <div>
          <label className="text-white text-xs font-medium mb-1 block">
            Slippage (%)
          </label>
          <input
            type="number"
            value={slippagePercent}
            onChange={(e) => setSlippagePercent(e.target.value)}
            className="w-full bg-white border border-gray-200 rounded px-2 py-1.5 text-black text-sm focus:outline-none focus:border-[#24c35b]/50 hover:border-[#24c35b]/40"
            placeholder="35"
            step="0.1"
            min="0.1"
            max="100"
          />
        </div>
      </div>

      {/* Balance Summary */}
      {selectedWallets.size > 0 && (
        <div className="p-1.5 rounded border border-gray-200 bg-gray-50">
          <h5 className="text-black text-xs font-medium mb-1">Summary</h5>
          
          {/* Basic Configuration */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2">
            <div className="flex justify-between">
              <span className="text-gray-500">SOL per trade:</span>
              <span className="text-black font-mono">{parseFloat(solAmountPerTrade) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Max trades:</span>
              <span className="text-black font-mono">{parseFloat(maxTradesToExecute) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Wallets:</span>
              <span className="text-black font-mono">{selectedWallets.size}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Trade type:</span>
              <span className={`font-mono ${tradeType === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                {tradeType.toUpperCase()}
              </span>
            </div>
          </div>

          {/* Budget Calculations */}
          <div className="grid grid-cols-2 gap-1 text-xs mb-2 pb-1 border-b border-gray-600/30">
            <div className="flex justify-between">
              <span className="text-gray-500">Total needed:</span>
              <span className="text-black font-mono font-bold">{calculateTotalSolNeeded().toFixed(3)} SOL</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Available:</span>
              <span className={`font-mono font-bold ${getTotalAvailableBalance() >= calculateTotalSolNeeded() ? 'text-[#24c35b]' : 'text-red-400'}`}>
                {getTotalAvailableBalance().toFixed(3)} SOL
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-2">
        <button
          onClick={() => setCreationStep('rules')}
          className="px-3 py-1.5 rounded border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-xs"
        >
          Back: Rules
        </button>
        <button
          onClick={handleFinishSetup}
          disabled={selectedWallets.size === 0 || getTotalAvailableBalance() < calculateTotalSolNeeded()}
          className="px-3 py-1.5 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs disabled:opacity-50"
        >
          {editingBotId ? 'Update Bot' : 'Finish Setup'}
        </button>
      </div>
    </div>
  );

  // Handle finishing bot setup (create or update)
  const handleFinishSetup = async () => {
    const effectiveWalletAddress = getEffectiveWalletAddress();
    if (!effectiveWalletAddress || !newBotName.trim() || newBotRules.length === 0 || !selectedToken) {
      return;
    }

    try {
      // Convert local rules format to service format
      const serviceConditions: Omit<AutoTraderCondition, 'id' | 'rule_id' | 'created_at'>[] = newBotRules.map(rule => ({
        field: rule.field,
        operator: rule.operator,
        value: rule.value
      }));

      const configurationMetadata = {
        tokenAddress: selectedToken.mint,
        tokenSymbol: selectedToken.symbol,
        tokenName: selectedToken.name,
        tokenImageUri: selectedToken.image_uri, // Store the image URL
        tradeType,
        isAutoTrader: true,
        slippage: parseFloat(slippagePercent),
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      if (editingBotId) {
        // Update existing bot
        await autoTraderRulesService.updateAutoTraderRule(
          effectiveWalletAddress,
          editingBotId,
          { 
            name: newBotName,
            tokenAddress: selectedToken!.mint,
            tokenSymbol: selectedToken!.symbol ?? undefined,
            tokenName: selectedToken!.name ?? undefined,
            tradeType,
            conditions: serviceConditions, 
            solAmountPerTrade: parseFloat(solAmountPerTrade), 
            maxTradesToExecute: parseInt(maxTradesToExecute), 
            slippagePercent: parseFloat(slippagePercent), 
            tradingWalletAddresses: Array.from(selectedWallets), 
            totalSolBudget: calculateTotalSolNeeded(), 
            configurationMetadata 
          }
        );
        console.log(`✅ Updated auto trader bot: ${newBotName} with ${newBotRules.length} rule(s) and slippage ${slippagePercent}%`);
      } else {
        // Create new bot/rule in the database (inactive by default)
        await autoTraderRulesService.createAutoTraderRule(
          effectiveWalletAddress,
          newBotName,
          selectedToken!.mint,
          selectedToken!.symbol || 'Unknown',
          selectedToken!.name || 'Unknown Token',
          tradeType,
          serviceConditions,
          false, // Start in inactive/stopped state
          { 
            solAmountPerTrade: parseFloat(solAmountPerTrade), 
            maxTradesToExecute: parseInt(maxTradesToExecute), 
            slippagePercent: parseFloat(slippagePercent), 
            tradingWalletAddresses: Array.from(selectedWallets), 
            totalSolBudget: calculateTotalSolNeeded(), 
            configurationMetadata 
          }
        );
        console.log(`✅ Created new auto trader bot: ${newBotName} with ${newBotRules.length} rule(s) and slippage ${slippagePercent}% (inactive by default)`);
      }

      // Reload rules and statistics to show the changes
      await loadAutoTraderRules();
      await loadRuleStatistics();

      // Reset creation state and go back to monitor
      setIsCreatingBot(false);
      setActiveTab('monitor');
      setNewBotName('');
      setNewBotRules([]);
      setCreationStep('token');
      setEditingBotId(null);
      setSelectedToken(null);
      setTradeType('buy');

    } catch (error) {
      console.error('Error saving auto trader bot:', error);
      // Could add toast notification here
    }
  };

  // Initialize WebSocket connection and component
  useEffect(() => {
    const initializeConnection = async () => {
      setIsLoading(true);
      setConnectionStatus('connecting');
      
      try {
        // Ensure WebSocket connection
        const connected = await wsService.ensureConnection();
        
        if (connected) {
          setConnectionStatus('connected');
          console.log('🔗 Advanced Auto Trader WebSocket connected');
        } else {
          setConnectionStatus('error');
          console.error('❌ Advanced Auto Trader WebSocket connection failed');
        }
      } catch (error) {
        console.error('Error initializing auto trader connection:', error);
        setConnectionStatus('error');
      } finally {
        setIsLoading(false);
      }
    };

    initializeConnection();

    // Load component data
    loadTradingWallets();
    loadAutoTraderRules();
    loadRuleStatistics();
    loadTradeHistory();
    setupRealtimeSubscription();

    // Cleanup function to unsubscribe from all tokens when component unmounts
    return () => {
      console.log('🧹 Advanced Auto Trader cleanup: Unsubscribing from all tokens');
      tokenSubscriptionsRef.current.forEach((cleanupFunctions, tokenAddress) => {
        cleanupFunctions.forEach(cleanup => cleanup());
      });
      tokenSubscriptionsRef.current.clear();
      subscribedTokensRef.current.clear();
      
      // Clean up realtime subscription
      if (realtimeSubscriptionRef.current) {
        adminSupabase.removeChannel(realtimeSubscriptionRef.current);
        realtimeSubscriptionRef.current = null;
      }
    };
  }, []);

  // Update refs when state changes
  useEffect(() => {
    autoTraderRulesRef.current = autoTraderRules;
  }, [autoTraderRules]);

  useEffect(() => {
    isTradingActiveRef.current = isTradingActive;
  }, [isTradingActive]);

  // ADDED: Quick balance refresh when wallet changes but trading wallets are already loaded
  useEffect(() => {
    const effectiveWallet = getEffectiveWalletAddress();
    
    // If we have trading wallets already loaded but wallet changed, just refresh balances
    if (effectiveWallet && tradingWallets.length > 0 && !isLoadingWallets) {
      console.log('🔄 Wallet changed with existing trading wallets - refreshing balances only');
      refreshTradingWalletBalances();
    }
  }, [walletUser.walletAddress, walletUser.walletProvider, tradingWallets.length, isLoadingWallets]);

  // Monitor connection status changes
  useEffect(() => {
    const checkConnectionStatus = () => {
      const status = wsService.getConnectionStatus();
      if (status !== connectionStatus) {
        setConnectionStatus(status);
        console.log(`🔄 Advanced Auto Trader connection status: ${status}`);
      }
    };

    const interval = setInterval(checkConnectionStatus, 5000);
    return () => clearInterval(interval);
  }, [connectionStatus]);

  // OPTIMIZED: Memoize the refresh handler to prevent unnecessary re-renders
  const handleRefreshTransactions = useCallback(() => {
    loadTradeHistory();
    loadRuleStatistics();
  }, []);

  // OPTIMIZED: Memoize the transaction list to prevent unnecessary re-renders
  const memoizedTransactionList = useMemo(() => {
    if (isLoadingTradeHistory) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
          <span className="text-gray-400 text-xs">Loading trade history...</span>
        </div>
      );
    }

    // Filter out failed transactions - only show successful and pending trades
    const successfulTrades = tradeHistory.filter(trade => trade.status !== 'failed');

    if (successfulTrades.length === 0) {
      return (
        <div className="text-center py-6">
          <History size={24} className="text-gray-500 mx-auto mb-2" />
          <p className="text-gray-400 text-xs">No successful trades yet</p>
          <p className="text-gray-500 text-xs mt-1">Successful auto trader trades will appear here</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 flex-1 overflow-y-auto">
        {successfulTrades.map((trade, index) => (
          <TransactionCard key={trade.id || `${trade.token_address}-${index}`} trade={trade} index={index} />
        ))}
      </div>
    );
  }, [tradeHistory, isLoadingTradeHistory]);

  // OPTIMIZED: Memoize the entire transactions tab to prevent unnecessary re-renders
  const memoizedTransactionsTab = useMemo(() => (
    <div className="flex flex-col h-full space-y-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <h4 className="text-white text-sm font-semibold">Successful Trades</h4>
        <button
          onClick={handleRefreshTransactions}
          className="px-3 py-1 rounded bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] text-xs hover:bg-[#24c35b]/30 transition-all"
        >
          <History size={12} className="mr-1 inline" />
          Refresh
        </button>
      </div>
      {memoizedTransactionList}
    </div>
  ), [handleRefreshTransactions, memoizedTransactionList]);

  // Main content for widget
  const content = (
    <div
      className={`w-full h-full flex flex-col bg-white overflow-hidden border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`}
    >
      <style>
        {globalWidgetTextStyles}
        {`
          /* Hide number input spinners */
          input[type="number"]::-webkit-outer-spin-button,
          input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          
          input[type="number"] {
            -moz-appearance: textfield;
          }
        `}
      </style>
      
      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#24c35b]"></div>
          <Bot size={18} className="text-[#24c35b]" />
          <h2 className="text-lg font-bold text-black">Advanced Auto Trader</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-black transition-colors p-1 rounded hover:bg-gray-100"
            title="Close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      {!isCreatingBot && (
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setActiveTab('monitor')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'monitor'
                ? 'text-[#24c35b] border-[#24c35b] bg-white'
                : 'text-gray-600 border-transparent hover:text-black hover:bg-gray-100'
            }`}
          >
            <Target size={16} className="inline mr-2" />
            Monitor
          </button>
          <button
            onClick={() => setActiveTab('transactions')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'transactions'
                ? 'text-[#24c35b] border-[#24c35b] bg-white'
                : 'text-gray-600 border-transparent hover:text-black hover:bg-gray-100'
            }`}
          >
            <History size={16} className="inline mr-2" />
            Transactions
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-grow overflow-y-auto px-4 py-3" style={{ background: '#ffffff', borderRadius: 0 }}>
        {isCreatingBot ? (
          // Creation flow
          <>
            {creationStep === 'token' && renderTokenSelection()}
            {creationStep === 'rules' && renderRulesCreation()}
            {creationStep === 'config' && renderConfigCreation()}
          </>
        ) : (
          // Normal tabs
          <>
            {activeTab === 'monitor' && (
              <div className="space-y-4">
                {/* Status and Controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${isTradingActive ? 'bg-[#24c35b]' : 'bg-gray-500'}`} />
                    <span className="text-black text-sm font-medium">
                      {isTradingActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsCreatingBot(true)}
                    className="px-3 py-1.5 rounded-lg bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30 transition-all text-xs"
                  >
                    <Plus size={12} className="mr-1 inline" />
                    Add Bot
                  </button>
                </div>

                {/* Rules List */}
                {isLoadingRules ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader size={16} className="text-[#24c35b] animate-spin mr-2" />
                    <span className="text-gray-400 text-xs">Loading auto trader bots...</span>
                  </div>
                ) : autoTraderRules.length === 0 ? (
                  <div className="text-center py-6">
                    <Bot size={24} className="text-gray-500 mx-auto mb-2" />
                    <p className="text-gray-400 text-xs">No auto trader bots configured</p>
                    <p className="text-gray-500 text-xs mt-1">Create your first bot to start automated trading</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {autoTraderRules.map(rule => {
                      const stats = ruleStatistics.get(rule.id) || {triggers: 0, success: 0, failed: 0};
                      
                      return (
                        <div
                          key={rule.id}
                          className="p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-all"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`w-2.5 h-2.5 rounded-full ${rule.isActive ? 'bg-[#24c35b]' : 'bg-gray-500'}`} />
                              <span className="text-black text-sm font-medium">{rule.name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                rule.tradeType === 'buy' 
                                  ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                                  : 'bg-red-500/20 text-red-400 border border-red-500/40'
                              }`}>
                                {rule.tradeType?.toUpperCase() || 'BUY'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleRuleToggle(rule.id)}
                                className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                                  rule.isActive 
                                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/40' 
                                    : 'bg-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/30 border border-[#24c35b]/40'
                                }`}
                              >
                                {rule.isActive ? 'Stop' : 'Start'}
                              </button>
                              <button
                                onClick={async () => {
                                  // Handle edit - populate form and enter creation mode
                                  setNewBotName(rule.name);
                                  setNewBotRules([...rule.conditions]);
                                  setEditingBotId(rule.id);
                                  
                                  // Set token and trade type - try to get image from stored metadata or fetch fresh
                                  if (rule.tokenAddress && rule.tokenSymbol) {
                                    // First try to use stored image from configuration metadata
                                    const storedImageUri = rule.configurationMetadata?.tokenImageUri;
                                    
                                    let selectedTokenData: Token;
                                    if (storedImageUri) {
                                      // Use stored image data
                                      selectedTokenData = {
                                        id: rule.tokenAddress,
                                        mint: rule.tokenAddress,
                                        symbol: rule.tokenSymbol,
                                        name: rule.tokenName || rule.tokenSymbol,
                                        created_at: new Date().toISOString(),
                                        image_uri: storedImageUri,
                                        price_usd: 0,
                                        liquidity_usd: 0,
                                        market_cap_usd: 0,
                                        holders: null,
                                        market: null
                                      } as Token;
                                      setSelectedToken(selectedTokenData);
                                    } else {
                                      // Try to fetch fresh token data to get the image
                                      const tokenData = await fetchTokenData(rule.tokenAddress);
                                      
                                      if (tokenData && tokenData.token) {
                                        // Use fetched data with image
                                        selectedTokenData = {
                                          id: rule.tokenAddress,
                                          mint: rule.tokenAddress,
                                          symbol: tokenData.token.symbol || rule.tokenSymbol,
                                          name: tokenData.token.name || rule.tokenName || rule.tokenSymbol,
                                          created_at: new Date().toISOString(),
                                          image_uri: tokenData.token.image || null,
                                          price_usd: tokenData.pools?.[0]?.price?.usd || 0,
                                          liquidity_usd: tokenData.pools?.[0]?.liquidity?.usd || 0,
                                          market_cap_usd: tokenData.pools?.[0]?.marketCap?.usd || 0,
                                          holders: tokenData.holders || null,
                                          market: tokenData.pools?.[0]?.market || null
                                        } as Token;
                                      } else {
                                        // Fallback to stored data without image
                                        selectedTokenData = {
                                          id: rule.tokenAddress,
                                          mint: rule.tokenAddress,
                                          symbol: rule.tokenSymbol,
                                          name: rule.tokenName || rule.tokenSymbol,
                                          created_at: new Date().toISOString(),
                                          image_uri: null,
                                          price_usd: 0,
                                          liquidity_usd: 0,
                                          market_cap_usd: 0,
                                          holders: null,
                                          market: null
                                        } as Token;
                                      }
                                      setSelectedToken(selectedTokenData);
                                    }
                                  }
                                  setTradeType(rule.tradeType || 'buy');
                                  
                                  // Load saved configuration
                                  if (rule.solAmountPerTrade) setSolAmountPerTrade(rule.solAmountPerTrade.toString());
                                  if (rule.maxTradesToExecute) setMaxTradesToExecute(rule.maxTradesToExecute.toString());
                                  if (rule.slippagePercent) setSlippagePercent(rule.slippagePercent.toString());
                                  if (rule.tradingWalletAddresses) setSelectedWallets(new Set(rule.tradingWalletAddresses));
                                  
                                  setIsCreatingBot(true);
                                  setCreationStep('token');
                                }}
                                className="text-gray-400 hover:text-white transition-colors"
                              >
                                <Edit2 size={12} />
                              </button>
                              <button
                                onClick={() => {
                                  setRuleToDelete({ id: rule.id, name: rule.name, isActive: rule.isActive });
                                  setShowDeleteConfirm(true);
                                }}
                                className="text-gray-400 hover:text-red-400 transition-colors"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Token Info */}
                          <div className="mb-2">
                            <div className="text-xs text-gray-600">
                              Token: <span className="text-black">{rule.tokenSymbol || 'Unknown'}</span>
                              {rule.tokenName && (
                                <span className="text-gray-500 ml-1">({rule.tokenName})</span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              {rule.tokenAddress}
                            </div>
                          </div>

                          {/* Conditions */}
                          <div className="mb-2">
                            <div className="text-xs text-gray-600 mb-1">Conditions:</div>
                            <div className="space-y-1">
                              {rule.conditions.map((condition, idx) => (
                                <div key={idx} className="text-xs text-gray-700">
                                  {AUTO_TRADER_FIELD_LABELS[condition.field] || condition.field} {condition.operator} {condition.value}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Statistics */}
                          <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-3">
                              <span className="text-gray-600">
                                Triggers: <span className="text-black">{stats.triggers}</span>
                              </span>
                              <span className="text-gray-600">
                                Success: <span className="text-[#24c35b]">{stats.success}</span>
                              </span>
                              <span className="text-gray-600">
                                Failed: <span className="text-red-400">{stats.failed}</span>
                              </span>
                            </div>
                            <div className="text-gray-500">
                              {new Date(rule.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transactions' && memoizedTransactionsTab}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && ruleToDelete && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#16161a] border border-gray-700/20 rounded-lg w-full max-w-md shadow-xl">
            <div className="p-4 border-b border-gray-700/20">
              <h3 className="text-white text-lg font-semibold">Delete Auto Trader Bot</h3>
            </div>
            <div className="p-4">
              <p className="text-gray-300 text-sm mb-4">
                Are you sure you want to delete the bot "{ruleToDelete.name}"?
                {ruleToDelete.isActive && (
                  <span className="block text-yellow-400 text-xs mt-2">
                    ⚠️ This bot is currently active and will stop trading.
                  </span>
                )}
              </p>
              <div className="flex items-center justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setRuleToDelete(null);
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-600/50 text-gray-400 hover:text-white hover:border-gray-500 transition-all text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (ruleToDelete) {
                      const effectiveWalletAddress = getEffectiveWalletAddress();
                      if (effectiveWalletAddress) {
                        try {
                          await autoTraderRulesService.deleteAutoTraderRule(effectiveWalletAddress, ruleToDelete.id);
                          await loadAutoTraderRules();
                          await loadRuleStatistics();
                          showNotification(`Auto trader bot "${ruleToDelete.name}" deleted successfully`, 'success');
                        } catch (error) {
                          showNotification(`Failed to delete bot: ${error}`, 'error');
                        }
                      }
                      setShowDeleteConfirm(false);
                      setRuleToDelete(null);
                    }
                  }}
                  className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 transition-all text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token Search Modal */}
      {isSearchModalOpen && (
        <TokenSearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
          onSelectToken={handleTokenSelect}
          referenceElement={searchButtonRef.current}
        />
      )}
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">{content}</div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`bg-[#16161a] border border-gray-700/20 rounded-lg w-full max-w-[420px] shadow-xl ${widgetClassName}`}>
        {content}
      </div>
    </div>
  );
};

// Factory function to create widget
export const createAdvancedAutoTraderWidget = () => {
  return {
    type: "advanced-auto-trader",
    title: "Advanced Auto Trader",
    content: <AdvancedAutoTrader isWidget={true} />,
    size: { width: 600, height: 600 },
    contentStyle: { 
      padding: 0,
      overflow: "hidden",
    },
    glassMorphism: true,
    containerStyle: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(12px)",
      borderRadius: "12px",
      border: "1px solid rgba(36, 195, 91, 0.2)",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
      overflow: "hidden",
    },
  };
};

export default AdvancedAutoTrader; 
